import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { Mail, Phone, FileText, Share2, Settings } from "lucide-react";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

// Extended fields: email_aliases, phone_aliases, notes, social_links, custom_fields
// None of these appear in current saved views' visibleColumns.
export const extendedContactFields = defineFieldConfigMap({
  // FIELD: email_aliases
  email_aliases: {
    type: "array",
    label: "Email Aliases",
    sortable: false,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: false,
    formSpan: 3,
    filterType: "contains",
    width: 250,
    minWidth: 200,
    maxWidth: 400,
    customComponent: "EmailAliasesEditor",
    placeholder: "Add alternative email addresses",
    icon: "Mail",
    // Array-specific configuration
    arrayConfig: {
      itemType: "email",
      allowCustom: true,
      maxItems: 10,
      validateItem: (item: string) => {
        const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return EMAIL_REGEX.test(item) || "Invalid email format";
      },
    },
    // Role-based access control - admin only
    allowedRoles: ["wtc_admin"],
    mobileDetailSectionId: "extended",
    graphqlSelector: "email_aliases",
  },
  phone_aliases: {
    type: "array",
    label: "Phone Aliases",
    sortable: false,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: false,
    formSpan: 3,
    filterType: "contains",
    width: 250,
    minWidth: 200,
    maxWidth: 400,
    customComponent: "PhoneAliasesEditor",
    placeholder: "Add shared company lines or alternate numbers",
    icon: "Phone",
    arrayConfig: {
      itemType: "phone",
      allowCustom: true,
      maxItems: 10,
    },
    allowedRoles: [...ALL_STAFF_ROLES],
    mobileDetailSectionId: "extended",
    graphqlSelector: "phone_aliases",
  },
  // FIELD: notes
  notes: {
    type: "textarea",
    label: "Notes",
    sortable: false,
    filterable: false,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 3,
    width: 250,
    minWidth: 200,
    maxWidth: 400,
    placeholder: "Enter notes about this contact",
    icon: "FileText",
    // Internal — staff only.
    allowedRoles: [...ALL_STAFF_ROLES],
    mobileDetailSectionId: "extended",
    graphqlSelector: "notes",
  },
  // FIELD: social_links
  social_links: {
    type: "json",
    label: "Social Links",
    sortable: false,
    filterable: false,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    placeholder: "Social media links",
    icon: "Share2",
    // Internal — staff only.
    allowedRoles: [...ALL_STAFF_ROLES],
    mobileDetailSectionId: "extended",
    graphqlSelector: "social_links",
  },
  // FIELD: custom_fields
  custom_fields: {
    type: "json",
    label: "Custom Fields",
    sortable: false,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    placeholder: "Custom field data",
    icon: "Settings",
    // Internal — staff only.
    allowedRoles: [...ALL_STAFF_ROLES],
    mobileDetailSectionId: "extended",
    graphqlSelector: "custom_fields",
  },
});
